import format from "date-fns/format";
import React, { useMemo, useState } from "react";
import Highlighter from "react-highlight-words";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import actions from "@/actions";
import ConfirmDialog from "@/components/ConfirmDialog";
import { GetTaskCommentResult } from "@/components/comment/actions/types";
import { CommentEditor } from "@/components/comment/components/CommentEditor";
import { CommentListModal } from "@/components/comment/components/CommentListModal";
import { useCommentListModal } from "@/components/comment/hooks/useCommentListModal";
import { CommentState } from "@/components/comment/reducers/comment";
import TaskEditorContainer from "@/containers/matrix/TaskEditorContainer";
import { Comment } from "@/models/comments";
import { RootState } from "@/reducers/types";

type CommentItem = Comment & { omit: boolean; rowSpan: number; insertDate: string };

type Props = {
  onRefresh: (option?: { delete?: boolean }) => void;
};

export const CommentGridBody = ({ onRefresh }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { comment, list } = useSelector<RootState, CommentState>((state: RootState) => state.comment);
  const construction = useSelector((state: RootState) => state.construction?.construction);

  const commentList = useMemo<CommentItem[]>(() => {
    const result: CommentItem[] = [];
    let prev: Comment | undefined = undefined;
    for (let i = 0; i < list.length; i++) {
      const c = list[i];
      // 行の結合があるか
      const omit = prev?.task_id === c.task_id;
      let rowSpan = 1;
      for (let j = i + 1; j < list.length; j++) {
        if (c.task_id === list[j].task_id) {
          rowSpan += 1;
        } else {
          break;
        }
      }
      // 登録日時日時（フォーマット）
      const insertDate = format(new Date(c.timestamp.insert_date), "yyyy-MM-dd HH:mm:ss");

      result.push({ ...c, omit, rowSpan, insertDate });
      prev = c;
    }

    return result;
  }, [list]);

  // 明細編集モーダル
  const [commentOfTaskToEdit, setCommentOfTaskToEdit] = useState<Comment>();

  // コメント一覧表示モーダル（コメント数列のクリックで開く）
  const commentListModal = useCommentListModal();

  // コメント編集
  const [commentToUpdate, setCommentToUpdate] =
    useState<Pick<Comment, "task_comment_id" | "comment" | "timestamp">>(null);
  const openCommentUpdateModal = (item: CommentItem) => {
    dispatch(
      actions.comment.getTaskComment({ task_comment_id: item.task_comment_id }, (res: GetTaskCommentResult) => {
        setCommentToUpdate(res);
      })
    );
  };
  const closeCommentUpdateModal = () => setCommentToUpdate(null);
  const updateComment = (text: string) => {
    dispatch(
      actions.comment.updateTaskComment(
        {
          task_comment_id: commentToUpdate.task_comment_id,
          comment: text,
          timestamp: commentToUpdate.timestamp,
        },
        () => {
          onRefresh();
          closeCommentUpdateModal();
        }
      )
    );
  };

  // コメント削除
  const [commentToDelete, setCommentToDelete] = useState<CommentItem>(null);
  const openCommentDeleteModal = (item: CommentItem) => setCommentToDelete(item);
  const closeCommentDeleteModal = () => setCommentToDelete(null);
  const deleteComment = () => {
    dispatch(
      actions.comment.deleteTaskComment(commentToDelete, () => {
        onRefresh({ delete: true });
        closeCommentDeleteModal();
      })
    );
  };

  return (
    <>
      <div className="overflow-y-scroll h-[calc(100%-30px)]" /*高さはテーブルヘッダの分を引く*/>
        <table className="tbl-basic tbl-data table-fixed">
          <tbody>
            {commentList.map((item: CommentItem) => {
              return (
                <tr key={`comment-item-${item.task_comment_id}`}>
                  {!item.omit && (
                    <>
                      {/* 機器分類 */}
                      <td rowSpan={item.rowSpan} className="w-110">
                        {item.category_name}
                      </td>
                      {/* 件名 */}
                      <td rowSpan={item.rowSpan} className="w-100">
                        <a
                          href={`/#/${construction?.construction_id}/matrix?category_id=${item.category_id}${
                            item.primary_charge_id ? "&primary_charge_id=" + item.primary_charge_id : ""
                          }&item_id=${item.item_id}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {item.item_name}
                        </a>
                      </td>
                      {/* 作業名 */}
                      <td rowSpan={item.rowSpan} className="w-80">
                        {item.process_name}
                      </td>
                      {/* 明細 */}
                      <td rowSpan={item.rowSpan} className="w-80 text-center">
                        <a onClick={() => setCommentOfTaskToEdit(item)}>{t("detail")}</a>
                      </td>
                      {/* コメント数 */}
                      <td rowSpan={item.rowSpan} className="w-80 text-center">
                        {item.comment_num}
                        <a className="ml-4" onClick={() => commentListModal.open(item)}>
                          {t("display")}
                        </a>
                      </td>
                    </>
                  )}
                  {/* コメント */}
                  <td className="w-full">
                    {item.comment?.split("\n").map((text, index) =>
                      text ? (
                        <Highlighter
                          key={index}
                          highlightClassName="comment-search-highlight"
                          searchWords={[comment]}
                          autoEscape={true}
                          textToHighlight={text}
                        />
                      ) : (
                        <p key={index}>
                          <br />
                        </p>
                      )
                    )}
                  </td>
                  {/* 登録日時 */}
                  <td className="w-160">{item.insertDate}</td>
                  {/* 登録者 */}
                  <td className="w-100">{item.user_name}</td>
                  {/* 編集 */}
                  <td className="w-80 text-center">
                    {item.roles[0] ? <a onClick={() => openCommentUpdateModal(item)}>{t("edit")}</a> : <></>}
                  </td>
                  {/* 削除 */}
                  <td className="w-80 text-center">
                    {item.roles[1] ? <a onClick={() => openCommentDeleteModal(item)}>{t("delete")}</a> : <></>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* 明細編集モーダル */}
      {commentOfTaskToEdit && (
        <TaskEditorContainer
          taskId={commentOfTaskToEdit.task_id}
          itemName={commentOfTaskToEdit.item_name}
          processName={commentOfTaskToEdit.process_name}
          categoryId={commentOfTaskToEdit.category_id}
          disableReloadProgressRate={true}
          closeHandler={() => setCommentOfTaskToEdit(null)}
          onSubmit={onRefresh}
        />
      )}
      {/* コメント一覧表示モーダル */}
      {commentListModal.isOpen && (
        <CommentListModal
          comments={commentListModal.comments}
          itemName={commentListModal.itemName}
          processName={commentListModal.processName}
          onClose={commentListModal.close}
        />
      )}
      {/* コメント編集モーダル */}
      {commentToUpdate && (
        <CommentEditor comment={commentToUpdate.comment} onClose={closeCommentUpdateModal} onSubmit={updateComment} />
      )}
      {/* コメント削除モーダル */}
      {commentToDelete && (
        <ConfirmDialog
          title={t("confirmation")}
          messages={[t("comment") + t("delete_confirmation"), t("is_it_ok")]}
          okClickHandler={deleteComment}
          cancelClickHandler={closeCommentDeleteModal}
        />
      )}
    </>
  );
};
